<template>
  <sticky-header-layout
    v-loading="isPageLoading"
  >
    <template #header>
      <list-page-header>
        <div slot="left">
          <div
            class="d-flex mb-2"
            style="max-width: 300px;"
          >
            <el-input
              v-model="brandSlugQuery"
              placeholder="Enter brand slug"
              prefix-icon="el-icon-search"
              size="mini"
              clearable
            />
            <el-button
              :loading="isPageLoading || isLoading"
              class="ml-2"
              size="mini"
              type="primary"
              icon="el-icon-search"
              circle
              @click="handleSearch"
            />
          </div>
          <el-pagination
            slot="left"
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            background
            layout="total, sizes, pager, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div
          slot="right"
          class="button-container"
        >
          <el-button
            class="add-button"
            type="primary"
            icon="el-icon-edit"
            @click="handleClickCreateButton"
          >{{ $t('button.create') }}</el-button>
        </div>
      </list-page-header>
    </template>

    <custom-table
      :loading="isLoading"
      :data="list"
      :column-list="columnList"
    >
      <template #columns="{ data, type }">
        <template v-if="isList(data)">
          <list-data-column
            :list="data"
            :type="type"
          />
        </template>
        <template v-else>
          {{ data }}
        </template>
      </template>

      <action-column
        slot="action-column"
        is-edit
        is-delete
        @edit="handleClickEditButton"
        @delete="handleClickDeleteButton"
      />
    </custom-table>
  </sticky-header-layout>
</template>

<script>
import { createComponent, onBeforeMount, ref } from '@vue/composition-api'

import ActionColumn from '../../components/table/ActionColumn.vue'
import CustomTable from '../../components/table/CustomTable.vue'
import ListDataColumn from '../../components/table/ListDataColumn.vue'
import ListPageHeader from '../layout/components/ListPageHeader.vue'

import BrandsService from '../../api/brands'
import { columnList } from '../../const/brand'
import useTablePaging from '../../hooks/useTablePaging'
import { tableListHandler } from '../../utils/form'

const BrandList = createComponent({
  name: 'BrandList',
  components: {
    ActionColumn,
    CustomTable,
    ListDataColumn,
    ListPageHeader,
  },
  setup(props, { root }) {
    const { $router, $confirm } = root

    const brandSlugQuery = ref('')
    const isPageLoading = ref(false)
    const {
      isLoading,
      list,
      listQuery,
      getList,
      handleSizeChange,
      handlePageChange,
      handleQueryParamsUpdate,
    } = useTablePaging({ service: BrandsService.getList, listHandler: tableListHandler })

    onBeforeMount(() => {
      getList()
    })

    const deleteBrand = async (id) => {
      isPageLoading.value = true
      try {
        await BrandsService.delete(id)
        handlePageChange(1)
        getList()
      } catch (error) {
        console.error(error)
      } finally {
        isPageLoading.value = false
      }
    }

    const handleClickCreateButton = () => {
      $router.push({ path: '/brand/create' })
    }
    const handleClickEditButton = ({ id }) => {
      $router.push({ path: `/brand/edit/${id}` })
    }
    const handleClickDeleteButton = ({ id }) => {
      $confirm(root.$t('message.confirmToDelete'), root.$t('common.warning'), {
        confirmButtonText: root.$t('button.delete'),
        cancelButtonText: root.$t('button.cancel'),
        confirmButtonClass: 'el-button--danger',
        type: 'warning',
      }).then(() => {
        deleteBrand(id)
      })
    }
    const handleSearch = () => {
      handleQueryParamsUpdate({
        page: 1,
        slug: brandSlugQuery.value,
      })
      getList()
    }

    const isList = data => Array.isArray(data)

    return {
      columnList,

      isLoading,
      isPageLoading,
      brandSlugQuery,
      list,
      listQuery,
      handleSizeChange,
      handlePageChange,

      handleClickCreateButton,
      handleClickEditButton,
      handleClickDeleteButton,
      handleSearch,
      isList,
    }
  },
})

export default BrandList

</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

::v-deep {
  .el-tag {
    white-space: normal;
    height: auto;
    word-break: break-word;
  }
}
</style>
